// src/pages/Accounting/Receipts/components/ReviewMode/index.tsx

import { useEffect, useMemo, useState } from "react";
import type { Receipt } from "../../types";
import { PALETTE } from "../../constants";
import { ReviewTop, ReviewFoot } from "./ReviewChrome";
import { ReceiptPaper } from "./ReceiptPaper";
import { ReviewForm, ReviewNotes, type ReviewDraft } from "./ReviewForm";
import { SuggestedMatch } from "./SuggestedMatch";

interface ReviewModeProps {
  receipts: Receipt[];
  onApprove: (
    receiptId: string,
    draft: ReviewDraft,
    keepMatch: boolean,
  ) => void;
  onReject: (receiptId: string) => void;
  onSkip: (receiptId: string) => void;
  onClose: () => void;
}

export function ReviewMode({
  receipts,
  onApprove,
  onReject,
  onSkip,
  onClose,
}: ReviewModeProps) {
  const queue = useMemo(
    () => receipts.filter((r) => r.matchStatus === "needs-review"),
    [receipts],
  );

  const [currentIndex, setCurrentIndex] = useState(0);
  const [matchDecision, setMatchDecision] = useState<"keep" | "unmatch" | null>(
    null,
  );
  const [draft, setDraft] = useState<ReviewDraft>(makeInitialDraft(queue[0]));

  const current = queue[currentIndex];
  const isLast = currentIndex >= queue.length - 1;
  const isComplete = queue.length === 0 || currentIndex >= queue.length;

  useEffect(() => {
    setDraft(makeInitialDraft(current));
    setMatchDecision(null);
  }, [currentIndex, current?.id]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key === "ArrowRight") {
        if (!current) return;
        const keepMatch =
          matchDecision === "keep" ||
          (matchDecision === null && !!current.matchedTransaction);
        onApprove(current.id, draft, keepMatch);
        advance();
        return;
      }
      if (e.key === "ArrowLeft" && currentIndex > 0) {
        setCurrentIndex((i) => Math.max(0, i - 1));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, draft, matchDecision, current?.id]);

  function handleApprove() {
    if (!current) return;
    const keepMatch =
      matchDecision === "keep" ||
      (matchDecision === null && !!current.matchedTransaction);
    onApprove(current.id, draft, keepMatch);
    advance();
  }

  function handleSkip() {
    if (!current) return;
    onSkip(current.id);
    advance();
  }

  function handleReject() {
    if (!current) return;
    onReject(current.id);
    advance();
  }

  function advance() {
    setCurrentIndex((i) =>
      i + 1 >= queue.length ? queue.length : i + 1,
    );
  }

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(14, 26, 26, 0.55)",
          zIndex: 60,
        }}
      />
      <div
        role="dialog"
        aria-label="Review receipts"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: PALETTE.panel,
          zIndex: 70,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <ReviewTop
          currentIndex={Math.min(currentIndex, Math.max(0, queue.length - 1))}
          totalCount={queue.length}
          onClose={onClose}
        />

        {isComplete ? (
          <AllCaughtUp onClose={onClose} totalProcessed={queue.length} />
        ) : current ? (
          <>
            <div
              style={{
                flex: 1,
                display: "grid",
                gridTemplateColumns: "1.1fr 1fr",
                minHeight: 0,
              }}
            >
              <ReceiptPaper receipt={current} />
              <div style={{ padding: "20px 28px", overflowY: "auto" }}>
                <ReviewForm draft={draft} onDraftChange={setDraft} />
                <SuggestedMatch
                  matched={current.matchedTransaction}
                  isConfirmed={matchDecision === "keep"}
                  onKeepMatch={() => setMatchDecision("keep")}
                  onUnmatch={() => setMatchDecision("unmatch")}
                  onPickDifferent={() =>
                    console.log("Pick different transaction clicked")
                  }
                />
                <ReviewNotes
                  value={draft.notes}
                  onChange={(v) => setDraft({ ...draft, notes: v })}
                />
              </div>
            </div>

            <ReviewFoot
              isLast={isLast}
              onApprove={handleApprove}
              onSkip={handleSkip}
              onReject={handleReject}
            />
          </>
        ) : null}
      </div>
    </>
  );
}

function AllCaughtUp({
  onClose,
  totalProcessed,
}: {
  onClose: () => void;
  totalProcessed: number;
}) {
  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: 40,
        textAlign: "center",
      }}
    >
      <div
        style={{
          fontSize: 48,
          color: PALETTE.vela,
          fontWeight: 700,
          marginBottom: 12,
        }}
      >
        ✓
      </div>
      <div
        style={{
          fontSize: 22,
          fontWeight: 700,
          color: PALETTE.ink,
          marginBottom: 6,
        }}
      >
        All caught up
      </div>
      <div
        style={{
          fontSize: 14,
          color: PALETTE.ink2,
          marginBottom: 20,
          fontWeight: 500,
        }}
      >
        You reviewed {totalProcessed} receipt
        {totalProcessed === 1 ? "" : "s"}. Nice work.
      </div>
      <button
        type="button"
        onClick={onClose}
        style={{
          background: "#0E1A1A",
          color: "#fff",
          border: 0,
          fontWeight: 600,
          fontSize: 13,
          padding: "10px 18px",
          borderRadius: 10,
          cursor: "pointer",
          boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
        }}
      >
        Close
      </button>
    </div>
  );
}

function makeInitialDraft(receipt: Receipt | undefined): ReviewDraft {
  if (!receipt) {
    return {
      vendorName: "",
      date: "",
      total: 0,
      tax: {},
      category: "",
      notes: "",
    };
  }
  return {
    vendorName: receipt.vendorName || "",
    date: receipt.date || "",
    total: receipt.total,
    tax: { ...receipt.tax },
    category: receipt.category || "",
    notes: receipt.notes || "",
  };
}